<template>
  <Card v-if="movie" class="max-w-md mx-auto">
    <div class="flex">
      <div class="w-1/3">
        <AspectRatio :ratio="2 / 3">
          <NuxtImg
            v-if="movie.meta?.offlineMediaResources?.cover_photo || movie.poster"
            :src="movie.meta?.offlineMediaResources?.cover_photo || movie.poster || undefined"
            :alt="movie.title"
            class="rounded-l-lg object-cover w-full h-full"
            loading="lazy"
            width="150"
            :height="225"
          />
          <div v-else class="flex items-center justify-center w-full h-full bg-muted rounded-l-lg">
            <Icon name="heroicons:film" class="w-8 h-8 text-muted-foreground" />
          </div>
        </AspectRatio>
      </div>
      <div class="w-2/3 p-4">
        <div class="flex justify-between items-start">
          <div>
            <h4 class="font-semibold">{{ movie.title }}</h4>
            <p class="text-sm text-gray-500">{{ movie.year }} • {{ movie.imdbRating }}</p>
          </div>
          <Button variant="ghost" size="icon" @click="$emit('refresh')">
            <Icon name="heroicons:arrow-path" class="w-4 h-4" />
          </Button>
        </div>
        <p class="text-sm text-gray-600 mt-1">{{ getActors(movie) }}</p>
        <div class="mt-2 flex flex-wrap gap-1">
          <span
            v-for="genre in getGenres(movie)"
            :key="genre"
            class="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full"
          >
            {{ genre }}
          </span>
        </div>
      </div>
    </div>
  </Card>
  <div v-else class="text-center py-4">
    <p class="text-gray-600">No movies available</p>
  </div>
</template>

<script setup lang="ts">
import type { MovieBubble } from '~/stores/interfaces'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

defineProps<{
  movie: MovieBubble | null
}>()

defineEmits(['refresh'])

function getActors(movie: MovieBubble): string {
  if (!movie.actors || movie.actors.length === 0) return 'No actors listed'
  return movie.actors.length > 2
    ? `${movie.actors[0]}, ${movie.actors[1]}, ...`
    : movie.actors.join(', ')
}

function getGenres(movie: MovieBubble): string[] {
  if (!movie.genre) return []
  if (Array.isArray(movie.genre)) return movie.genre
  return (movie.genre as string).split(',').map((g: string) => g.trim())
}
</script>
